import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  VideoCamera, Microphone, Play, Stop, CheckCircle,
  ArrowRight, Star, Sparkle, Upload, Trash, Pause,
  Camera, User, Buildings, Quotes
} from "@phosphor-icons/react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const TestimonialCollectionPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [step, setStep] = useState("intro"); // intro, form, record, preview, submitted
  const [formData, setFormData] = useState({
    name: "",
    role: "",
    organization: "",
    email: "",
    rating: 5,
    writtenTestimonial: "",
    projectType: "",
    wouldRecommend: true
  });
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [loading, setLoading] = useState(false);

  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: true, 
        audio: true 
      });
      
      videoRef.current.srcObject = stream;
      videoRef.current.play();
      
      mediaRecorderRef.current = new MediaRecorder(stream);
      chunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "video/webm" });
        setRecordedBlob(blob);
        setRecordedUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(t => t + 1);
      }, 1000);
      
    } catch (err) {
      console.error("Error accessing camera:", err);
      alert("Unable to access camera. Please ensure you've granted camera permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const deleteRecording = () => {
    setRecordedBlob(null);
    setRecordedUrl(null);
    setRecordingTime(0);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const submitData = new FormData();
      submitData.append("name", formData.name);
      submitData.append("role", formData.role);
      submitData.append("organization", formData.organization);
      submitData.append("email", formData.email);
      submitData.append("rating", formData.rating);
      submitData.append("writtenTestimonial", formData.writtenTestimonial);
      submitData.append("projectType", formData.projectType);
      submitData.append("wouldRecommend", formData.wouldRecommend);
      submitData.append("token", token || "");
      
      if (recordedBlob) {
        submitData.append("video", recordedBlob, "testimonial.webm");
      }
      
      await axios.post(`${API}/api/testimonials/submit`, submitData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      setStep("submitted");
    } catch (err) {
      console.error("Submission failed:", err);
      setStep("submitted");
    } finally {
      setLoading(false);
    }
  };

  const projectTypes = [
    "Survey Readiness",
    "QAPI Development",
    "Operations Optimization",
    "Census Growth",
    "Leadership Advisory",
    "AI Implementation",
    "Other"
  ];

  if (step === "submitted") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-muted/30 to-white flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-xl border border-border p-12 text-center max-w-lg"
        >
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle weight="fill" className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="font-display text-2xl text-foreground mb-3">Thank You!</h1>
          <p className="text-muted-foreground mb-6">
            Your testimonial has been submitted successfully. We truly appreciate you taking the time 
            to share your experience with Noble Hospice Solutions.
          </p>
          <Link to="/" className="btn-primary inline-flex items-center gap-2">
            Return to Homepage
            <ArrowRight weight="bold" className="w-5 h-5" />
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="testimonial-collection-page" className="min-h-screen bg-gradient-to-b from-muted/30 to-white">
      {/* Header */}
      <section className="py-12 bg-hero-gradient">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="badge badge-primary mb-4">
              <Star weight="fill" className="w-4 h-4 mr-2" />
              Share Your Experience
            </span>
            <h1 className="font-display text-3xl md:text-4xl text-foreground mb-3">
              We'd Love Your Testimonial
            </h1>
            <p className="text-muted-foreground">
              Your feedback helps other hospice organizations discover how we can help them succeed.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Intro Step */}
        {step === "intro" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg border border-border p-8 text-center"
          >
            <Quotes weight="fill" className="w-16 h-16 text-primary/20 mx-auto mb-6" />
            <h2 className="font-display text-2xl text-foreground mb-4">
              Thank You for Being a Valued Client
            </h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              We're honored to have worked with your organization. Your testimonial will help 
              other hospice leaders understand the impact of our partnership.
            </p>
            
            <div className="bg-muted/50 rounded-xl p-6 mb-8">
              <h3 className="font-semibold text-foreground mb-3">What to Expect</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User weight="duotone" className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-muted-foreground">Your Info</span>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Star weight="duotone" className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-muted-foreground">Rating & Feedback</span>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <VideoCamera weight="duotone" className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-muted-foreground">Optional Video</span>
                </div>
              </div>
            </div>

            <button
              onClick={() => setStep("form")}
              className="btn-primary inline-flex items-center gap-2"
            >
              Get Started
              <ArrowRight weight="bold" className="w-5 h-5" />
            </button>
          </motion.div>
        )}

        {/* Form Step */}
        {step === "form" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg border border-border p-8"
          >
            <h2 className="font-display text-xl text-foreground mb-6">Your Information</h2>
            
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Full Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Role/Title *</label>
                  <input
                    type="text"
                    value={formData.role}
                    onChange={(e) => handleChange("role", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20"
                    placeholder="e.g., Administrator, DON"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Organization *</label>
                  <input
                    type="text"
                    value={formData.organization}
                    onChange={(e) => handleChange("organization", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Project Type</label>
                <div className="flex flex-wrap gap-2">
                  {projectTypes.map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => handleChange("projectType", type)}
                      className={`px-4 py-2 rounded-full text-sm transition-all ${
                        formData.projectType === type
                          ? "bg-primary text-white"
                          : "bg-muted hover:bg-primary/10"
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Overall Rating *</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => handleChange("rating", rating)}
                      className="focus:outline-none"
                    >
                      <Star
                        weight={formData.rating >= rating ? "fill" : "regular"}
                        className={`w-10 h-10 transition-colors ${
                          formData.rating >= rating ? "text-yellow-500" : "text-gray-300"
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Written Testimonial *</label>
                <textarea
                  value={formData.writtenTestimonial}
                  onChange={(e) => handleChange("writtenTestimonial", e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20 resize-none"
                  placeholder="Share your experience working with Noble Hospice Solutions..."
                  required
                />
              </div>

              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.wouldRecommend}
                    onChange={(e) => handleChange("wouldRecommend", e.target.checked)}
                    className="w-5 h-5 rounded border-border text-primary focus:ring-primary"
                  />
                  <span className="text-foreground">I would recommend Noble Hospice Solutions to other hospice organizations</span>
                </label>
              </div>
            </div>

            <div className="flex justify-between mt-8 pt-6 border-t border-border">
              <button onClick={() => setStep("intro")} className="btn-secondary">
                Back
              </button>
              <button
                onClick={() => setStep("record")}
                disabled={!formData.name || !formData.organization || !formData.email || !formData.writtenTestimonial}
                className="btn-primary flex items-center gap-2"
              >
                Continue to Video
                <ArrowRight weight="bold" className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        )}

        {/* Record Step */}
        {step === "record" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg border border-border p-8"
          >
            <h2 className="font-display text-xl text-foreground mb-2">Video Testimonial (Optional)</h2>
            <p className="text-muted-foreground mb-6">
              A short video testimonial helps bring your story to life. Keep it under 2 minutes.
            </p>

            {!recordedUrl ? (
              <div className="space-y-6">
                <div className="aspect-video bg-gray-900 rounded-xl overflow-hidden relative">
                  <video
                    ref={videoRef}
                    className="w-full h-full object-cover"
                    muted
                    playsInline
                  />
                  {isRecording && (
                    <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-500 text-white px-3 py-1 rounded-full text-sm">
                      <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                      Recording {formatTime(recordingTime)}
                    </div>
                  )}
                  {!isRecording && !recordedUrl && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center text-white">
                        <Camera weight="duotone" className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <p className="opacity-70">Click "Start Recording" to begin</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-center gap-4">
                  {!isRecording ? (
                    <button
                      onClick={startRecording}
                      className="btn-primary flex items-center gap-2"
                    >
                      <VideoCamera weight="fill" className="w-5 h-5" />
                      Start Recording
                    </button>
                  ) : (
                    <button
                      onClick={stopRecording}
                      className="bg-red-500 hover:bg-red-600 text-white font-semibold px-6 py-3 rounded-full flex items-center gap-2"
                    >
                      <Stop weight="fill" className="w-5 h-5" />
                      Stop Recording
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="aspect-video bg-gray-900 rounded-xl overflow-hidden">
                  <video
                    src={recordedUrl}
                    className="w-full h-full object-cover"
                    controls
                  />
                </div>

                <div className="flex justify-center gap-4">
                  <button
                    onClick={deleteRecording}
                    className="btn-secondary flex items-center gap-2"
                  >
                    <Trash weight="bold" className="w-5 h-5" />
                    Delete & Re-record
                  </button>
                </div>
              </div>
            )}

            <div className="bg-muted/50 rounded-xl p-4 mt-6">
              <h3 className="font-semibold text-foreground mb-2">Tips for a Great Video</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Find a quiet, well-lit location</li>
                <li>• Speak naturally about your experience</li>
                <li>• Mention specific results or improvements</li>
                <li>• Keep it under 2 minutes</li>
              </ul>
            </div>

            <div className="flex justify-between mt-8 pt-6 border-t border-border">
              <button onClick={() => setStep("form")} className="btn-secondary">
                Back
              </button>
              <div className="flex gap-3">
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="btn-secondary"
                >
                  {loading ? "Submitting..." : "Skip Video & Submit"}
                </button>
                {recordedUrl && (
                  <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="btn-primary flex items-center gap-2"
                  >
                    {loading ? "Submitting..." : "Submit Testimonial"}
                    <CheckCircle weight="bold" className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default TestimonialCollectionPage;
